import { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, PhoneOff, Mic, MicOff, Video, VideoOff, Volume2, VolumeX } from "lucide-react";
import { CallState, CallType, IncomingCall } from "@/hooks/useWebRTC";

interface CallOverlayProps {
  callState: CallState;
  callType: CallType;
  partnerName: string;
  isMuted: boolean;
  isCameraOff: boolean;
  isSpeakerOn?: boolean;
  incomingCall: IncomingCall | null;
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  onAnswer: () => void;
  onReject: () => void;
  onEnd: () => void;
  onToggleMute: () => void;
  onToggleCamera: () => void;
  onToggleSpeaker?: () => void;
}

const CallOverlay = ({
  callState, callType, partnerName, isMuted, isCameraOff, isSpeakerOn = true,
  incomingCall, localStream, remoteStream,
  onAnswer, onReject, onEnd, onToggleMute, onToggleCamera, onToggleSpeaker,
}: CallOverlayProps) => {
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  // earpiece output (audio element → earpiece on mobile)
  const remoteAudioRef = useRef<HTMLAudioElement>(null);
  // loudspeaker output (hidden video element → loudspeaker on mobile, including iOS Safari)
  const remoteSpeakerRef = useRef<HTMLVideoElement>(null);

  // Attach local stream to local preview video
  useEffect(() => {
    if (localVideoRef.current && localStream) {
      localVideoRef.current.srcObject = localStream;
    }
  }, [localStream]);

  // Single unified effect: routes the remote stream to the correct output element.
  //
  // Mobile browser audio routing rules:
  //   <audio>        → earpiece (quiet, phone-to-ear mode)
  //   <video>        → loudspeaker (always, including on iOS Safari)
  //
  // For video calls: remote <video> carries both picture and audio — no secondary element needed.
  // For audio calls: swap between a hidden <audio> (earpiece) and a hidden <video> (speaker)
  //   depending on isSpeakerOn. This is the standard web workaround for iOS Safari.
  useEffect(() => {
    if (!remoteStream) return;

    if (callType === "video") {
      // Video call: video element handles audio+video. Clear the audio-only elements.
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = remoteStream;
        remoteVideoRef.current.play().catch(() => {});
      }
      if (remoteAudioRef.current) remoteAudioRef.current.srcObject = null;
      if (remoteSpeakerRef.current) remoteSpeakerRef.current.srcObject = null;
    } else {
      // Audio call: route to earpiece (audio) or loudspeaker (video) based on toggle
      if (isSpeakerOn) {
        if (remoteSpeakerRef.current) {
          remoteSpeakerRef.current.srcObject = remoteStream;
          remoteSpeakerRef.current.play().catch(() => {});
        }
        if (remoteAudioRef.current) remoteAudioRef.current.srcObject = null;
      } else {
        if (remoteAudioRef.current) {
          remoteAudioRef.current.srcObject = remoteStream;
          remoteAudioRef.current.play().catch(() => {});
        }
        if (remoteSpeakerRef.current) remoteSpeakerRef.current.srcObject = null;
      }
    }
  }, [remoteStream, callType, isSpeakerOn]);

  // Always-rendered hidden output elements so srcObject is never lost on re-render
  const hiddenOutputEls = (
    <>
      {/* earpiece path */}
       <audio
         ref={remoteAudioRef}
         autoPlay
         playsInline
         aria-hidden="true"
         className="absolute h-px w-px opacity-0 pointer-events-none"
       />
       {/* Keep the media element in the document without display:none, which
           prevents audio playback in some mobile browsers. */}
       <video
         ref={remoteSpeakerRef}
         autoPlay
         playsInline
         muted={false}
         aria-hidden="true"
         className="absolute h-px w-px opacity-0 pointer-events-none"
       />
    </>
  );

  if (callState === "idle") {
    // Still render the hidden output elements so refs stay valid
    return <>{hiddenOutputEls}</>;
  }

  const displayName = partnerName || incomingCall?.callerName || "?";
  const initial = displayName.charAt(0).toUpperCase();

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[100] bg-gray-950 flex flex-col"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        {/* Hidden output elements always rendered so srcObject survives state changes */}
        {hiddenOutputEls}

        {/* ── Incoming ring screen ── */}
        {callState === "incoming" && incomingCall && (
          <div className="flex-1 flex flex-col items-center justify-center gap-8 p-8">
            <motion.div
              className="w-28 h-28 rounded-full gradient-primary flex items-center justify-center shadow-glow"
              animate={{ scale: [1, 1.08, 1] }}
              transition={{ repeat: Infinity, duration: 1.4 }}
            >
              <span className="text-5xl text-primary-foreground font-extrabold">{initial}</span>
            </motion.div>

            <div className="text-center">
              <p className="text-white text-2xl font-extrabold">{incomingCall.callerName}</p>
              <p className="text-white/50 text-sm mt-1.5">
                Incoming {incomingCall.callType === "video" ? "video" : "voice"} call…
              </p>
            </div>

            <div className="flex gap-14 mt-2">
              <div className="flex flex-col items-center gap-2">
                <button
                  onClick={onReject}
                  className="h-16 w-16 rounded-full bg-red-500 flex items-center justify-center shadow-lg active:scale-95 transition-transform"
                >
                  <PhoneOff className="h-7 w-7 text-white" />
                </button>
                <span className="text-white/50 text-xs">Decline</span>
              </div>
              <div className="flex flex-col items-center gap-2">
                <motion.button
                  onClick={onAnswer}
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ repeat: Infinity, duration: 1.1 }}
                  className="h-16 w-16 rounded-full bg-green-500 flex items-center justify-center shadow-lg active:scale-95"
                >
                  {incomingCall.callType === "video"
                    ? <Video className="h-7 w-7 text-white" />
                    : <Phone className="h-7 w-7 text-white" />}
                </motion.button>
                <span className="text-white/50 text-xs">Accept</span>
              </div>
            </div>
          </div>
        )}

        {/* ── Active call (calling or connected) ── */}
        {(callState === "calling" || callState === "connected") && (
          <>
            <div className="flex-1 relative bg-gray-900 overflow-hidden">

              {callType === "video" ? (
                // Remote video (carries audio too — no separate audio element needed)
                <video
                  ref={remoteVideoRef}
                  autoPlay
                  playsInline
                  className="w-full h-full object-cover"
                />
              ) : (
                // Audio-only avatar
                <div className="w-full h-full flex flex-col items-center justify-center gap-5">
                  <motion.div
                    className="w-28 h-28 rounded-full gradient-primary flex items-center justify-center shadow-glow"
                    animate={callState === "calling" ? { scale: [1, 1.06, 1] } : {}}
                    transition={{ repeat: Infinity, duration: 1.5 }}
                  >
                    <span className="text-5xl text-primary-foreground font-extrabold">{initial}</span>
                  </motion.div>
                  <p className="text-white text-xl font-extrabold">{displayName}</p>
                  <p className="text-white/50 text-sm">
                    {callState === "calling" ? "Calling…" : "Connected"}
                  </p>
                </div>
              )}

              {/* Local video PiP (video calls only) */}
              {callType === "video" && localStream && (
                <div className="absolute top-4 right-4 w-28 h-40 rounded-2xl overflow-hidden border-2 border-white/20 shadow-xl">
                  <video
                    ref={localVideoRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-full h-full object-cover scale-x-[-1]"
                  />
                </div>
              )}

              {/* Status badge */}
              <div className={`absolute top-4 left-4 rounded-full px-3 py-1.5 ${callState === "connected" ? "bg-green-500/80" : "bg-black/50 backdrop-blur-sm"}`}>
                <p className="text-white text-xs font-bold">
                  {callState === "calling" ? "Calling…" : "Connected"}
                </p>
              </div>
            </div>

            {/* Controls bar */}
            <div className="bg-gray-950 px-6 py-7 safe-area-bottom">
              <div className="flex items-center justify-center gap-5">

                {/* Mute mic */}
                <div className="flex flex-col items-center gap-1.5">
                  <button
                    onClick={onToggleMute}
                    className={`h-14 w-14 rounded-full flex items-center justify-center transition-all active:scale-90 ${isMuted ? "bg-white/30" : "bg-white/10"}`}
                  >
                    {isMuted ? <MicOff className="h-6 w-6 text-white" /> : <Mic className="h-6 w-6 text-white" />}
                  </button>
                  <span className="text-white/40 text-[10px]">{isMuted ? "Unmute" : "Mute"}</span>
                </div>

                {/* Speaker toggle (audio calls only) */}
                {callType === "audio" && onToggleSpeaker && (
                  <div className="flex flex-col items-center gap-1.5">
                    <button
                      onClick={onToggleSpeaker}
                      className={`h-14 w-14 rounded-full flex items-center justify-center transition-all active:scale-90 ${!isSpeakerOn ? "bg-white/30" : "bg-white/10"}`}
                    >
                      {isSpeakerOn ? <Volume2 className="h-6 w-6 text-white" /> : <VolumeX className="h-6 w-6 text-white" />}
                    </button>
                    <span className="text-white/40 text-[10px]">{isSpeakerOn ? "Speaker on" : "Speaker off"}</span>
                  </div>
                )}

                {/* End call */}
                <div className="flex flex-col items-center gap-1.5">
                  <button
                    onClick={onEnd}
                    className="h-16 w-16 rounded-full bg-red-500 flex items-center justify-center shadow-lg active:scale-90 transition-transform"
                  >
                    <PhoneOff className="h-7 w-7 text-white" />
                  </button>
                  <span className="text-white/40 text-[10px]">End</span>
                </div>

                {/* Camera toggle (video calls only) */}
                {callType === "video" && (
                  <div className="flex flex-col items-center gap-1.5">
                    <button
                      onClick={onToggleCamera}
                      className={`h-14 w-14 rounded-full flex items-center justify-center transition-all active:scale-90 ${isCameraOff ? "bg-white/30" : "bg-white/10"}`}
                    >
                      {isCameraOff ? <VideoOff className="h-6 w-6 text-white" /> : <Video className="h-6 w-6 text-white" />}
                    </button>
                    <span className="text-white/40 text-[10px]">{isCameraOff ? "Cam off" : "Camera"}</span>
                  </div>
                )}

              </div>
            </div>
          </>
        )}

        {/* ── Call ended ── */}
        {callState === "ended" && (
          <div className="flex-1 flex flex-col items-center justify-center gap-4">
            <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center">
              <PhoneOff className="h-8 w-8 text-white/40" />
            </div>
            <p className="text-white/50 text-lg font-bold">Call ended</p>
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

export default CallOverlay;
